import { useRouter } from 'next/router';
import { HTMLAttributes, useState } from 'react';
import Drawer from '@/dashboard/components/Drawer/index';
import { QuoteWidget } from '@/dashboard/components/QuoteWidget';
import { useOpenGetQuote } from '@/dashboard/hooks/useOpenGetQuote';
import { usePageJump } from '@/dashboard/hooks/usePageJump';
import { Divider } from '@/dashboard/mui';
import { getHomeDocumentsPath } from '@/dashboard/utils/routes';
import {
  FolderOpenIcon,
  SparklesIcon,
  ViewGridIcon,
} from '@heroicons/react/outline';
import { Menu } from '@mui/icons-material';
import {
  Box,
  Button,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
} from '@mui/material';
import clsx from 'clsx';
import { lintrk } from 'nextjs-linkedin-insight-tag';
import { useAnalytics } from 'use-analytics';

export const MyPoliciesKey = 'My policies';
export const DocumentsKey = 'Documents';
export const ExploreCommunityKey = 'Explore community';

const LinkedinConversionId = process.env.NX_LINKEDIN_CONVERSION_ID;

type HomeMobileDrawerMenuProps = HTMLAttributes<HTMLDivElement>;
const HomeMobileDrawerMenu = (props: HomeMobileDrawerMenuProps) => {
  const { className } = props;
  const router = useRouter();
  const [open, setOpen] = useState(false);
  const [buttonList, setButtonList] = useState<
    { iconType: number; key: string }[]
  >([
    { iconType: 1, key: MyPoliciesKey },
    { iconType: 2, key: DocumentsKey },
    { iconType: 3, key: ExploreCommunityKey },
  ]);
  const { track } = useAnalytics();
  const { openGetQuoteIframe, setOpenGetQuoteIframe } = useOpenGetQuote();
  const { jumpToHomePage } = usePageJump();

  /**
   * methods
   */
  const getButtonIcon = (iconType) => {
    let icon = null;
    switch (iconType) {
      case 1:
        icon = <ViewGridIcon />;
        break;
      case 2:
        icon = <FolderOpenIcon />;
        break;
      case 3:
        icon = <SparklesIcon />;
        break;
      default:
        break;
    }
    return icon;
  };

  /**
   * methods
   */
  const showMenuAction = () => {
    setOpen(true);
  };

  // close drawer
  const closeMenuAction = () => {
    setOpen(false);
  };

  // item click
  const itemClickAction = (item) => {
    closeMenuAction();
    switch (item.key) {
      case MyPoliciesKey:
        jumpToHomePage();
        break;
      case DocumentsKey:
        router.replace(getHomeDocumentsPath());
        break;
      case ExploreCommunityKey:
        window.location.href = process.env.NX_COMMUNITY_URL;
        break;
      default:
        break;
    }
  };

  // get a quote
  const getQuoteAction = () => {
    track('click_get_quote_button');
    lintrk('track', { conversion_id: LinkedinConversionId });
    setOpen(false);
    setOpenGetQuoteIframe(true);
  };

  return (
    <Box className={clsx(className)}>
      <IconButton onClick={showMenuAction}>
        <Menu className="h-7 w-7" />
      </IconButton>
      {open && (
        <Drawer
          width="300px"
          close={closeMenuAction}
          className="bg-black-secondary"
          closeBtnClassName="text-white"
        >
          <Box className="flex flex-col">
            <List className="rounded-lg">
              {buttonList.map((item, index) => {
                return (
                  <Box
                    key={item.key}
                    className="mx-3 mb-3 flex flex-col rounded-lg"
                  >
                    <ListItem
                      disablePadding
                      onClick={() => itemClickAction(item)}
                    >
                      <ListItemButton className="mx-2 rounded-lg">
                        <ListItemIcon className="h-6 w-6 min-w-[40px] text-white">
                          {getButtonIcon(item.iconType)}
                        </ListItemIcon>
                        <ListItemText
                          primary={
                            <Typography className="font-bold text-white">
                              {item.key}
                            </Typography>
                          }
                        />
                      </ListItemButton>
                    </ListItem>
                    <Divider />
                  </Box>
                );
              })}
            </List>
            <Button
              className="mx-2 rounded-full border-2 font-bold"
              variant="contained"
              onClick={getQuoteAction}
            >
              Get new quote
            </Button>
          </Box>
        </Drawer>
      )}
      {openGetQuoteIframe && <QuoteWidget isOpen={openGetQuoteIframe} />}
    </Box>
  );
};

export default HomeMobileDrawerMenu;
